import SidePage from '@/shared/SidePage'
import { CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { IEquipment, IEquipmentEvent, IEquipmentEventInfo } from '@/pages/Equipment/equipment.types.ts'
import equipmentApi from '@/pages/Equipment/equipment.api.ts'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import equipmentUtils from '@/pages/Equipment/equipment.utils.ts'
import timeUtils from '@/utils/time.utils.ts'
import SeparateLine from '@/shared/Forms/SeparateLine'
import EmptyPlaceholder from '@/shared/Common/EmptyPlaceholder'
import Button from '@/shared/Buttons/Button'
import GoToLinkIcon from '@/assets/icons/goToLink.svg?react'
import AnalysisIcon from '@/assets/icons/search.svg?react'
import AiIcon from '@/assets/icons/ai-icon.svg?react'
import { useNavigate } from 'react-router-dom'
import urls from '@/navigation/urls.ts'
import css from './equipmentEventSidePage.module.css'
import ReactMarkdown from 'react-markdown'
import TextStatisticCard from '@/shared/Analytics/TextStatisticCard'
import formatUtils from '@/utils/format.utils'
import QuestionIconWithTooltip from '@/shared/Common/QuestionIconWithTooltip'
import Tag, { TTagColor } from '@/shared/Tags/Tag'
import SmoothDropdown from '@/shared/Animated/SmoothDropdown/SmoothDropdown'
import Skeleton from '@/shared/Loaders/Skeleton'

const ASSISTANT_VISIBLE_LINES = 6
const ASSISTANT_FADE_LINES = 2
const ASSISTANT_LINE_HEIGHT = 22
const ASSISTANT_PREVIEW_HEIGHT = (ASSISTANT_VISIBLE_LINES + ASSISTANT_FADE_LINES) * ASSISTANT_LINE_HEIGHT
const ASSISTANT_FADE_HEIGHT = ASSISTANT_FADE_LINES * ASSISTANT_LINE_HEIGHT

const formatCount = (value?: number) => {
  if (!Number.isFinite(value)) return '-'
  return formatUtils.formatNumber(value)
}

const formatCountWithWord = (value: number, words: [string, string, string]) => {
  return `${formatCount(value)} ${formatUtils.numWord(value, words)}`
}

const formatPercent = (value?: number, decimals: number = 1) => {
  if (!Number.isFinite(value)) return '-'
  return `${(value * 100).toFixed(decimals).replace(/\.0+$/, '')}%`
}

type TActivityTone = 'rare' | 'irregular' | 'regular'

const getActivityModeMeta = (
  ratio: number,
  activePeriodsCount: number,
  recentEventsCount: number
): { title: string; tone: TActivityTone } => {
  if (ratio <= 0) return { title: 'Нет активности', tone: 'rare' }
  if (activePeriodsCount === 1) {
    return {
      title: recentEventsCount > 1 ? 'Ошибка произошла впервые за долгое время' : 'Ошибка произошла впервые',
      tone: 'rare',
    }
  }
  if (ratio <= 0.05) return { title: 'Редкие всплески', tone: 'rare' }
  if (ratio <= 0.25) return { title: 'Нерегулярная активность', tone: 'irregular' }
  return { title: 'Регулярная активность', tone: 'regular' }
}

const formatDuration = (value?: number) => {
  if (!Number.isFinite(value)) return '-'
  if (value <= 0) return '0с'

  const totalSeconds = Math.floor(value / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const parts = []
  if (days) parts.push(`${days}д`)
  if (hours) parts.push(`${hours}ч`)
  if (minutes) parts.push(`${minutes}м`)
  if (!parts.length) parts.push(`${seconds}с`)
  return parts.join(' ')
}

const formatDateTime = (value?: string) => {
  if (!value) return null
  return timeUtils.formatDate(value, 'dd MMM yyyy, HH:mm')
}

const formatDateOnly = (value?: string) => {
  if (!value) return null
  return timeUtils.formatDate(value, 'dd MMM yyyy')
}

const formatRangeLabel = (from?: string, to?: string) => {
  const start = formatDateOnly(from)
  const end = formatDateOnly(to)
  if (!start || !end) return null
  return `${start} - ${end}`
}

const formatTimeAgoSafe = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  const diffMs = Date.now() - date.getTime()
  if (diffMs <= 0) return 'только что'

  const totalMinutes = Math.floor(diffMs / 60000)
  if (totalMinutes < 1) return 'только что'

  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60

  const parts = []
  if (days) parts.push(`${days}д`)
  if (days || hours) parts.push(`${hours}ч`)
  parts.push(`${minutes}м`)

  return `${parts.join(' ')} назад`
}

interface IRatePeriodItem {
  key: string
  label: string
  title: string
  value: number
}

interface IRateBurstItem {
  key: string
  label: string
  subtitle: string
  value: string
  width: number
}

interface IRatePresentation {
  subtitle: string
  periodText: string | null
  highlight: string
  activityTone: TActivityTone
  activityTitle: string
  activityHint: string
  activityShareValue: string
  activityShareNote: string
  latestValue: string
  latestWord: string
  latestLabel: string
  latestHint: string
  peakValue: string
  peakWord: string
  peakLabel: string
  peakHint: string
  averageIntervalValue: string
  averageIntervalNote: string
  maxGapValue: string
  maxGapNote: string
  topBursts: IRateBurstItem[]
  empty: boolean
}

interface IProps {
  open: boolean
  onClose: () => void
  onCloseAnimationEnd?: () => void
  equipmentId: IEquipment['id']
  event?: IEquipmentEvent | null
}

interface IInnerProps extends Omit<IProps, 'event'> {
  event: IEquipmentEvent
}

const EquipmentEventSidePage = (props: IInnerProps) => {
  const navigate = useNavigate()
  const { wait, loading } = useHttpLoader()
  const [info, setInfo] = useState<IEquipmentEventInfo>(null)
  const [assistantExpanded, setAssistantExpanded] = useState(false)
  const [assistantExpandable, setAssistantExpandable] = useState(false)
  const assistantPreviewRef = useRef<HTMLDivElement>(null)

  const eventName = useMemo(() => {
    if (!props.event) return '-'

    switch (props.event.type) {
      case 'CONNECT':
      case 'DISCONNECT': {
        const typeName = equipmentUtils.getEquipmentEventTypeName(props.event.type)
        return typeName || props.event.type
      }
      case 'IN_MAINTENANCE':
        return 'Переведен в обслуживание'
      case 'FROM_MAINTENANCE_TO_NORMAL':
        return 'Переведен из обслуживания в нормальную работу'
      case 'LP_ERROR': {
        const code = props.event.payload?.errorCode
        const description = props.event.payload?.errorDescription
        return [code, description].filter(Boolean).join(': ') || 'Ошибка'
      }
      default:
        return props.event.type
    }
  }, [props.event])

  const eventColor = useMemo<TTagColor>(() => {
    switch (props.event.type) {
      case 'CONNECT':
        return 'green'
      case 'DISCONNECT':
        return 'red'
      case 'IN_MAINTENANCE':
      case 'FROM_MAINTENANCE_TO_NORMAL':
        return 'geekblue'
      case 'LP_ERROR':
        return props.event.payload?.flag === 'WARNING' ? 'orange' : 'red'
      default:
        return 'gray'
    }
  }, [props.event])

  const eventKind = useMemo(() => {
    switch (props.event.type) {
      case 'CONNECT':
      case 'DISCONNECT': {
        const typeName = equipmentUtils.getEquipmentEventTypeName(props.event.type)
        return typeName || 'Событие'
      }
      case 'IN_MAINTENANCE':
        return 'Режим обслуживания'
      case 'FROM_MAINTENANCE_TO_NORMAL':
        return 'Нормальная работа'
      case 'LP_ERROR': {
        const flag = props.event.payload?.flag
        if (flag === 'WARNING') return 'Предупреждение'
        if (flag === 'FATAL_FAULT') return 'Критическая ошибка'
        return 'Ошибка'
      }
      default:
        return 'Событие'
    }
  }, [props.event])

  useEffect(() => {
    if (!props.open || !props.event) return

    const errorCode = props.event.payload?.errorCode
    if (!errorCode) {
      setInfo(null)
      return
    }

    setInfo(null)
    wait(
      equipmentApi.getEquipmentEventInfo(props.equipmentId, {
        errorCode,
      }),
      (resp) => {
        if (resp.status === 'success') {
          setInfo(resp.body)
        }
      }
    )
  }, [props.open, props.event, props.equipmentId])

  useEffect(() => {
    setAssistantExpanded(false)
  }, [props.event?.id, props.open])

  const handleMonitoring = () => {
    const ts = new Date(props.event.createdAt).getTime()
    navigate(urls.liftMonitoring(props.equipmentId) + `?ts=${ts}`)
  }

  const handleAnalysis = () => {
    const ts = new Date(props.event.createdAt).getTime()
    navigate(urls.liftAnalysis(props.equipmentId) + `?ts=${ts}`)
  }

  const renderInfo = () => {
    if (!info) {
      return <EmptyPlaceholder inscription="Нет данных для отображения" />
    }

    const text = info.assistantOverview?.trim()

    if (!text) {
      return <p className={`text-nm ${css.infoText}`}>-</p>
    }

    return (
      <ReactMarkdown
        components={{
          p: ({ node, ...props }) => <p className={`text-nm ${css.infoText}`} {...props} />,
          li: ({ node, ...props }) => <li className={`text-nm ${css.infoText}`} {...props} />,
          ol: ({ node, ...props }) => <ol className={css.infoList} {...props} />,
          ul: ({ node, ...props }) => <ul className={css.infoList} {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-heading-5" style={{ marginTop: '12px' }} {...props} />,
          h4: ({ node, ...props }) => <h4 className="text-heading-5" style={{ marginTop: '12px' }} {...props} />,
          h5: ({ node, ...props }) => <h5 className="text-heading-5" style={{ marginTop: '12px' }} {...props} />,
        }}
      >
        {text}
      </ReactMarkdown>
    )
  }

  const assistantText = info?.assistantOverview?.trim() || ''
  const assistantUnavailable = assistantText.toLowerCase().includes('невозможно дать ответ')
  const shouldRenderAssistantCard = loading || !assistantUnavailable
  const assistantContent = !loading && shouldRenderAssistantCard ? renderInfo() : null
  const assistantPreviewStyle = useMemo(
    () =>
      ({
        '--assistant-preview-height': `${ASSISTANT_PREVIEW_HEIGHT}px`,
        '--assistant-preview-fade-height': `${ASSISTANT_FADE_HEIGHT}px`,
      }) as CSSProperties,
    []
  )
  const recentDates = useMemo(
    () => [...(info?.lastDates ?? [])].sort((a, b) => new Date(b).getTime() - new Date(a).getTime()).slice(0, 5),
    [info]
  )
  const dailySeries = useMemo(() => {
    if (!info?.eventRate?.daily?.length) return []
    return [...info.eventRate.daily].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }, [info])
  const dailyPeriods = useMemo<IRatePeriodItem[]>(
    () =>
      dailySeries.map((item) => ({
        key: item.date,
        label: formatDateOnly(item.date) || item.date,
        title: item.date,
        value: item.count,
      })),
    [dailySeries]
  )
  const rateRangeLabel = useMemo(() => formatRangeLabel(info?.eventRate?.from, info?.eventRate?.to), [info])
  const ratePresentation = useMemo<IRatePresentation>(() => {
    const periods = dailyPeriods
    const activePeriods = periods.filter((item) => item.value > 0)
    const latestItem = activePeriods[activePeriods.length - 1] || null
    const peakItem = activePeriods.length
      ? activePeriods.reduce((max, item) => (item.value > max.value ? item : max), activePeriods[0])
      : null
    const activeRatio = periods.length ? activePeriods.length / periods.length : 0
    const dominantShare = peakItem && info?.count ? peakItem.value / info.count : 0
    const activityMeta = getActivityModeMeta(activeRatio, activePeriods.length, info?.lastDates?.length || 0)
    const topBursts = [...activePeriods].sort((a, b) => b.value - a.value).slice(0, 5)
    const maxBurstValue = Math.max(...topBursts.map((item) => item.value), 0)
    const intervalCountValue = formatCount(info?.recurrence?.intervalsCount)
    const totalPeriodsLabel = formatCountWithWord(periods.length || 0, ['дня', 'дней', 'дней'])
    const activePeriodsLabel = formatCountWithWord(activePeriods.length, ['день', 'дня', 'дней'])

    return {
      subtitle: 'По дням',
      periodText: rateRangeLabel || 'За весь доступный период',
      highlight: `${activePeriodsLabel} с событиями из ${totalPeriodsLabel}`,
      activityTone: activityMeta.tone,
      activityTitle: activityMeta.title,
      activityHint: activePeriods.length
        ? `${activePeriodsLabel} с событиями за весь период`
        : 'В доступном диапазоне активные дни не обнаружены',
      activityShareValue: formatPercent(activeRatio),
      activityShareNote: `${activePeriodsLabel} из ${totalPeriodsLabel}`,
      latestValue: formatCount(latestItem?.value),
      latestWord: formatUtils.numWord(latestItem?.value || 0, ['событие', 'события', 'событий']),
      latestLabel: latestItem?.label || '-',
      latestHint: latestItem?.title ? formatTimeAgoSafe(latestItem.title) : 'Нет активных периодов',
      peakValue: formatCount(peakItem?.value),
      peakWord: formatUtils.numWord(peakItem?.value || 0, ['событие', 'события', 'событий']),
      peakLabel: peakItem?.label || '-',
      peakHint: peakItem
        ? `${formatPercent(dominantShare)} всех фиксаций пришлись на этот период`
        : 'Нет активных периодов',
      averageIntervalValue: formatDuration(info?.recurrence?.mtbfMs),
      averageIntervalNote:
        intervalCountValue !== '-' ? `${intervalCountValue} интервалов учтено` : 'Между соседними событиями',
      maxGapValue: formatDuration(info?.recurrence?.maxIntervalMs),
      maxGapNote: 'Самое долгое затишье',
      topBursts: topBursts.map((item) => ({
        key: item.key,
        label: item.label,
        subtitle: info?.count ? `${formatPercent(item.value / info.count)} от всех фиксаций` : '-',
        value: `${formatCount(item.value)} шт.`,
        width: maxBurstValue > 0 ? Math.max((item.value / maxBurstValue) * 100, item.value > 0 ? 12 : 0) : 0,
      })),
      empty: !activePeriods.length,
    }
  }, [dailyPeriods, info, rateRangeLabel])

  const renderSkeletonLine = (width: string, height: string = '16px') => (
    <Skeleton width={width} height={height} style={{ borderRadius: '4px' }} />
  )

  const renderAssistantSkeleton = () => (
    <div className={css.assistantBlock}>
      <div className={css.assistantHeader}>
        <AiIcon className={css.assistantIcon} />
        <h5 className="text-heading-4">AI-обзор</h5>
      </div>
      <div className={css.skeletonLines}>
        {renderSkeletonLine('94%')}
        {renderSkeletonLine('100%')}
        {renderSkeletonLine('91%')}
        {renderSkeletonLine('86%')}
        {renderSkeletonLine('68%')}
      </div>
    </div>
  )

  const renderRecentTimelineSkeleton = () => (
    <div className={css.recentTimelineSection}>
      <div className={css.sectionHeader}>
        <h5 className="text-heading-5">Последние фиксации события</h5>
      </div>
      <div className={css.skeletonTimelineList}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className={css.skeletonTimelineItem}>
            <Skeleton width="24px" height="24px" style={{ borderRadius: '50%' }} />
            <div className={css.skeletonLines}>
              {renderSkeletonLine(index === 0 ? '44%' : '52%', '18px')}
              {renderSkeletonLine(index === 0 ? '24%' : '28%', '14px')}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderActivePeriodsSkeleton = () => (
    <div className={css.dynamicsChartCard}>
      <div className={css.dynamicsChartHeader}>
        <div className={css.skeletonLines}>
          {renderSkeletonLine('180px', '24px')}
          {renderSkeletonLine('240px', '14px')}
        </div>
      </div>
      <div className={css.skeletonLines}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className={css.skeletonBurstItem}>
            <div className={css.burstHeader}>
              <div className={css.skeletonLines}>
                {renderSkeletonLine(index === 0 ? '140px' : '180px', '18px')}
                {renderSkeletonLine(index === 0 ? '120px' : '150px', '14px')}
              </div>
              {renderSkeletonLine('72px', '16px')}
            </div>
            {renderSkeletonLine('100%', '10px')}
          </div>
        ))}
      </div>
    </div>
  )

  const renderDynamicsSkeleton = () => (
    <div className={css.dynamicsPanel}>
      <div className={css.dynamicsHeader}>
        <div className={css.skeletonLines}>
          {renderSkeletonLine('170px', '24px')}
          {renderSkeletonLine('220px', '14px')}
        </div>
        {renderSkeletonLine('150px', '28px')}
      </div>

      <div className={css.dynamicsSignal}>
        <div className={css.skeletonLines}>
          {renderSkeletonLine('120px', '14px')}
          {renderSkeletonLine('260px', '34px')}
          {renderSkeletonLine('76%', '18px')}
        </div>
        <div className={css.skeletonSignalAside}>
          {renderSkeletonLine('100px', '14px')}
          {renderSkeletonLine('88px', '32px')}
          {renderSkeletonLine('110px', '14px')}
        </div>
      </div>

      <div className={css.dynamicsOverviewGrid}>
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className={css.dynamicsHeroCard}>
            <div className={css.skeletonLines}>
              {renderSkeletonLine(index === 0 ? '150px' : '160px', '14px')}
              {renderSkeletonLine('110px', '36px')}
              {renderSkeletonLine('70%', '18px')}
              {renderSkeletonLine('48%', '14px')}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  useLayoutEffect(() => {
    if (!assistantPreviewRef.current || !assistantText) {
      setAssistantExpandable(false)
      setAssistantExpanded(false)
      return
    }

    const contentHeight = assistantPreviewRef.current.scrollHeight
    const isExpandable = contentHeight > ASSISTANT_PREVIEW_HEIGHT + 8
    setAssistantExpandable(isExpandable)
    if (!isExpandable) {
      setAssistantExpanded(false)
    }
  }, [assistantText])

  return (
    <SidePage
      onClose={props.onClose}
      open={props.open}
      onCloseAnimationEnd={props.onCloseAnimationEnd}
      style={{ overflow: 'hidden' }}
    >
      <div className={css.wrapper}>
        <div className={css.header}>
          <div className={css.headerRow}>
            <div className={css.headerMeta}>
              <Tag color={eventColor}>{eventKind}</Tag>
              <p className="text-nm text-secondary">
                {timeUtils.formatDate(props.event.createdAt, 'dd MMM yyyy, HH:mm:ss') || '-'}
              </p>
            </div>
          </div>
          <h4 className={`text-heading-4 ${css.eventName}`}>{eventName}</h4>
          <div className={css.headerActions}>
            <Button size="small" type="default" text="Мониторинг" icon={<GoToLinkIcon />} onClick={handleMonitoring} />
            <Button size="small" type="tetriary" text="Анализ" icon={<AnalysisIcon />} onClick={handleAnalysis} />
            <QuestionIconWithTooltip
              text="Перейти к мониторингу или анализу в момент фиксации события"
              position="bottom"
              style={{ marginLeft: '8px' }}
            />
          </div>
        </div>
        <SeparateLine />

        <div className={css.scrollArea}>
          <div className={css.summaryGrid}>
            <TextStatisticCard title="Всего зафиксированно" loading={loading} style={{ height: 'fit-content' }}>
              <div className={css.statCardContent}>
                <span className="text-large-stat-2">{formatUtils.formatNumber(info?.count)} шт.</span>
              </div>
            </TextStatisticCard>
            <TextStatisticCard
              title="Среднее время между событиями"
              loading={loading}
              style={{ height: 'fit-content' }}
            >
              <span className="text-large-stat-2">{formatDuration(info?.recurrence?.mtbfMs)}</span>
            </TextStatisticCard>
          </div>

          {shouldRenderAssistantCard ? (
            <>
              <SeparateLine />
              <div className={css.section}>
                {loading ? (
                  renderAssistantSkeleton()
                ) : (
                  <div className={css.assistantBlock}>
                    <div className={css.assistantHeader}>
                      <AiIcon className={css.assistantIcon} />
                      <h5 className="text-heading-4">AI-обзор</h5>
                    </div>
                    <div
                      className={css.assistantContent}
                      data-expanded={assistantExpanded}
                      data-expandable={assistantExpandable}
                      style={assistantPreviewStyle}
                    >
                      <div
                        ref={assistantPreviewRef}
                        className={css.assistantPreview}
                        data-expanded={assistantExpanded}
                        data-expandable={assistantExpandable}
                      >
                        {assistantContent}
                      </div>
                      {assistantExpandable && (
                        <SmoothDropdown open={assistantExpanded} className={css.assistantDropdown}>
                          {assistantContent}
                        </SmoothDropdown>
                      )}
                    </div>
                    {assistantExpandable && (
                      <button
                        type="button"
                        className={`text-nm ${css.assistantToggle}`}
                        onClick={() => setAssistantExpanded((prev) => !prev)}
                        aria-expanded={assistantExpanded}
                      >
                        {assistantExpanded ? 'Свернуть' : 'Развернуть'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : null}

          {(loading || info) && (
            <>
              <SeparateLine />

              {loading ? (
                <>
                  <div className={css.section}>{renderRecentTimelineSkeleton()}</div>
                  <SeparateLine />
                </>
              ) : recentDates.length ? (
                <>
                  <div className={css.section}>
                    <div className={css.recentTimelineSection}>
                      <div className={css.sectionHeader}>
                        <h5 className="text-heading-5">Последние фиксации события</h5>
                      </div>

                      <div className={css.recentTimeline}>
                        {recentDates.map((date, index) => (
                          <div key={date} className={css.recentTimelineItem}>
                            <div className={css.recentTimelineMarker}>
                              <span className={`text-caption ${css.recentTimelineIndex}`}>{index + 1}</span>
                            </div>
                            <div className={css.recentTimelineContent}>
                              <div className={css.recentTimelineTitleRow}>
                                <p className="text-nm">{formatDateTime(date) || date}</p>
                                {index === 0 ? (
                                  <span className={`text-caption ${css.recentTimelineBadge}`}>Последнее</span>
                                ) : null}
                              </div>
                              <p className="text-sm text-secondary">{formatTimeAgoSafe(date)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <SeparateLine />
                </>
              ) : null}

              <div className={css.section}>
                {loading ? (
                  renderActivePeriodsSkeleton()
                ) : (
                  <div className={css.dynamicsChartCard}>
                    <div className={css.dynamicsChartHeader}>
                      <div>
                        <h5 className="text-heading-5">Активные периоды</h5>
                        <p className="text-sm text-secondary">Самые заметные всплески за весь диапазон</p>
                      </div>
                    </div>

                    {ratePresentation.empty ? (
                      <div className={css.rateEmptyState}>
                        <p className="text-nm text-secondary">Нет активных периодов в выбранном представлении</p>
                      </div>
                    ) : (
                      <div className={css.burstList}>
                        {ratePresentation.topBursts.map((item) => (
                          <div key={item.key} className={css.burstItem}>
                            <div className={css.burstHeader}>
                              <div className={css.burstMeta}>
                                <p className="text-nm">{item.label}</p>
                                <p className="text-sm text-secondary">{item.subtitle}</p>
                              </div>
                              <span className={`text-caption ${css.burstValue}`}>{item.value}</span>
                            </div>
                            <div className={css.burstTrack}>
                              <div className={css.burstFill} style={{ width: `${item.width}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <SeparateLine />

              <div className={css.section}>
                {loading ? (
                  renderDynamicsSkeleton()
                ) : (
                  <div className={css.dynamicsPanel}>
                    <div className={css.dynamicsHeader}>
                      <div className={css.dynamicsHeading}>
                        <h5 className="text-heading-5">Динамика событий</h5>
                        <p className="text-sm text-secondary">
                          {ratePresentation.subtitle}
                          {ratePresentation.periodText ? ` • ${ratePresentation.periodText}` : ''}
                        </p>
                      </div>
                      <span className={`text-caption ${css.dynamicsBadge}`}>{ratePresentation.highlight}</span>
                    </div>

                    <div className={css.dynamicsSignal} data-tone={ratePresentation.activityTone}>
                      <div className={css.dynamicsSignalMain}>
                        <p className="text-sm text-secondary">Характер активности</p>
                        <h4 className={css.dynamicsSignalTitle}>{ratePresentation.activityTitle}</h4>
                        <p className={`text-nm ${css.dynamicsSignalHint}`}>{ratePresentation.activityHint}</p>
                      </div>
                      <div className={css.dynamicsSignalAside}>
                        <p className="text-sm text-secondary">Доля активности</p>
                        <div className={css.dynamicsSignalValue}>{ratePresentation.activityShareValue}</div>
                        <p className="text-sm text-secondary">{ratePresentation.activityShareNote}</p>
                      </div>
                    </div>

                    <div className={css.dynamicsOverviewGrid}>
                      <div className={css.dynamicsHeroCard}>
                        <p className="text-sm text-secondary">Последний активный период</p>
                        <div className={css.dynamicsHeroValueRow}>
                          <span className={css.dynamicsHeroValue}>{ratePresentation.latestValue}</span>
                          <span className="text-nm text-secondary">{ratePresentation.latestWord}</span>
                        </div>
                        <p className="text-nm">{ratePresentation.latestLabel}</p>
                        <p className="text-sm text-secondary">{ratePresentation.latestHint}</p>
                      </div>

                      <div className={css.dynamicsHeroCard}>
                        <p className="text-sm text-secondary">Основной активный период</p>
                        <div className={css.dynamicsHeroValueRow}>
                          <span className={css.dynamicsHeroValue}>{ratePresentation.peakValue}</span>
                          <span className="text-nm text-secondary">{ratePresentation.peakWord}</span>
                        </div>
                        <p className="text-nm">{ratePresentation.peakLabel}</p>
                        <p className={`text-sm ${css.dynamicsHeroHint}`}>{ratePresentation.peakHint}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </SidePage>
  )
}

export default (props: IProps) => {
  if (!props.event) return null

  return <EquipmentEventSidePage {...(props as IInnerProps)} />
}

/**
 *
 */
